import { useCallback, useEffect, useMemo } from 'react';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useWorklistFiltersContext } from '../domains/worklist/WorklistFilters/WorklistFiltersContext';
import { useWorklistFiltersContextV2 } from '../domains/worklist/Worklist/contexts/WorklistFiltersContextV2';
import { useWorklistSearchContext } from '../domains/worklist/Worklist/contexts/WorklistSearchContext';
import { useGenerateFilterConfigV2 } from '../domains/worklist/WorklistFilters/hooks/useGenerateFilterConfigV2';
import { WorklistSurface, type WorklistSurfaceType } from '../domains/worklist/Worklist/types';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  AUTOLOAD_NEXT_CASE,
  GET_ME,
  GET_WORKLIST,
  UPDATE_WORKLIST_SETTINGS,
} from '../modules/Apollo/queries';
import {
  WorklistItemStatus,
  type AutoloadNextCaseMutation,
  type AutoloadNextCaseMutationVariables,
  type GetMeQuery,
  type GetMeQueryVariables,
  type GetWorklistQuery,
  type GetWorklistQueryVariables,
} from '../generated/graphql';
import { logger } from '../modules/logger';
import { useLocation } from 'react-router-dom';
import {
  worklistAutoLoadState,
  worklistAutoLoadStateDefault,
  activeCustomWorklistIDsState,
} from '../domains/worklist/Worklist/state';
import { useRecoilState } from 'recoil';
import { useClaimPatient } from './useClaimPatient';
import { PageTypes, getPageType } from '../utils/pageTypes';
import { useSavedSortingPreferences } from '../domains/worklist/Worklist/hooks/useSavedSortingPreferences';
import { useWorklistVariables } from '../domains/worklist/Worklist/hooks/useWorklistVariables';
import { processFiltersForQuery } from '../domains/worklist/WorklistFilters/utils';
import { getWorklistKey } from '../domains/worklist/Worklist/utils/worklistUtils';
import { performance } from 'utils/performance';
import {
  marks,
  measureAutoloadNextCaseEnd,
  measureAutoloadNextCaseStart,
  ReportExitAction,
} from 'domains/reporter/RichTextEditor/analytics/performance';
import { WithApolloContext } from 'modules/Apollo/types';
import { type ToastProps, type ToastKey } from 'common/ui/Toaster/Toast';
import { dispatchExtensionEvent, clearCaseEvent } from 'domains/extension/extensionEventCreators';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import { useManagePatientJacket } from 'hooks/useManagePatientJacket';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useToasterDispatch } from 'common/ui/Toaster';

export type HandleAutoloadNextCaseParams = {
  reportExitAction: ReportExitAction;
  defaultToastMsg: string;
  onAutoloadComplete?: (nextCaseSmid: string | null) => void;
  isAddendum?: boolean;
};

export type HandleAutoloadNextCaseResult = {
  nextCaseSmid: Nullable<string>;
  toastMsg: string;
  toastOptions: Partial<ToastProps>;
};

type UseWorklistAutoLoadReturn = {
  isWorklistAutoLoadEnabled: boolean;
  isWorklistAutoLoadToggleDisplayable: boolean;
  toggleWorklistAutoLoad: (prevSurface?: WorklistSurfaceType) => Promise<void>;
  readNextCase: (params: { reportExitAction: ReportExitAction }) => Promise<string | null>;
  handleAutoloadNextCase: (
    params: HandleAutoloadNextCaseParams
  ) => Promise<HandleAutoloadNextCaseResult>;
  loadDiscardedSmidsFromSessionStorage: () => Set<string>;
  saveDiscardedSmidsInSessionStorage: (discardedSmids: Set<string>) => void;
  removeDiscardedSmidInSessionStorage: (smidToRemove: string) => void;
};

const displayableSurfaces: WorklistSurfaceType[] = [WorklistSurface.CUSTOM_WORKLIST];

const SESSION_DISCARDED_SMIDS_KEY = 'auto-load-discarded-smids';
const AUTOLOAD_GENERIC_ERROR =
  'Something went wrong while attempting to autoload your next case. Please open your worklist to select one.';

// Archive (= backend "Completed") is deliberately excluded. Claiming a completed exam
// converts it into an addendum, so autoloading one silently starts an addendum nobody
// asked for. Addendums already underway stay eligible via InProgressAddendum.
// TODO: WF-1969 - Remove once FF.WORKLIST_AUTOLOAD_BACKEND reaches 100% (legacy FE autoload path only)
const AUTO_LOADABLE_STATUSES = [
  WorklistItemStatus.Pending,
  WorklistItemStatus.InProgress,
  WorklistItemStatus.InProgressAddendum,
];

// TODO: WF-1969 - Remove once FF.WORKLIST_AUTOLOAD_BACKEND reaches 100% (legacy FE autoload path only)
const isRetryableAutoloadError = (error: unknown): boolean => {
  if (error == null || typeof error !== 'object') return false;

  const safe = error as {
    message?: unknown;
    graphQLErrors?: Array<{ message?: unknown; extensions?: { code?: unknown } }>;
    networkError?: { message?: unknown };
  };

  const candidates: string[] = [];
  if (typeof safe.message === 'string') candidates.push(safe.message.toLowerCase());
  if (typeof safe.networkError?.message === 'string') {
    candidates.push(safe.networkError.message.toLowerCase());
  }
  safe.graphQLErrors?.forEach((e) => {
    if (typeof e?.message === 'string') candidates.push(e.message.toLowerCase());
    if (typeof e?.extensions?.code === 'string') candidates.push(e.extensions.code.toLowerCase());
  });

  return candidates.some(
    (m) =>
      m.includes('locked_by') ||
      m.includes('locked by') ||
      m.includes('already claimed') ||
      m.includes('currently being read by another user') ||
      m.includes('case_locked')
  );
};

// Convert the worklist table's GraphQL query variables into the autoload mutation
// input shape. Pagination + retrieval-only fields (cursor/limit/smids) are dropped —
// autoload picks across the full filtered result, not a fixed page. The cast handles
// codegen-level shape divergence: query variables permit scalar-or-array (GraphQL
// list-coercion); mutation inputs are strict arrays. Runtime values from
// useWorklistVariables are already arrays so the cast is sound.
const buildAutoloadInput = ({
  variables,
  currentCaseSmid,
  skippedSmids,
}: {
  variables: Nullable<GetWorklistQueryVariables>;
  currentCaseSmid: string | null;
  skippedSmids: string[];
}): AutoloadNextCaseMutationVariables['input'] => {
  const { cursor: _cursor, limit: _limit, smids: _smids, ...filterFields } = variables ?? {};
  return {
    ...(filterFields as unknown as AutoloadNextCaseMutationVariables['input']),
    currentCaseSmid,
    skippedSmids,
  };
};

const loadDiscardedSmidsFromSessionStorage = (): Set<string> => {
  try {
    const raw = sessionStorage.getItem(SESSION_DISCARDED_SMIDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
      return new Set(parsed);
    }
  } catch (e) {
    logger.warn(
      `[useWorklistAutoLoad] - Failed to parse sessionStorage key "${SESSION_DISCARDED_SMIDS_KEY}":`,
      e
    );
  }
  return new Set();
};

const saveDiscardedSmidsInSessionStorage = (discardedSmids: Set<string>): void => {
  sessionStorage.setItem(SESSION_DISCARDED_SMIDS_KEY, JSON.stringify(Array.from(discardedSmids)));
};

const removeDiscardedSmidInSessionStorage = (smidToRemove: string): void => {
  const currentSet = loadDiscardedSmidsFromSessionStorage();
  if (currentSet.has(smidToRemove)) {
    currentSet.delete(smidToRemove);
    saveDiscardedSmidsInSessionStorage(currentSet);
  }
};

export const useWorklistAutoLoad = ({ context }: WithApolloContext): UseWorklistAutoLoadReturn => {
  const [isWorklistAutoLoadFeatureEnabled] = useFeatureFlagEnabled(FF.WORKLIST_AUTO_LOAD);
  const [isRefactorEnabled] = useFeatureFlagEnabled(FF.WORKLIST_CONTEXT_REFACTOR);
  // When on, use the atomic backend autoloadNextCase mutation. When off, fall back
  // to the legacy FE lazy-query + client-side eligibility loop. Default off so the
  // legacy path is the safe rollout default until WORKLIST_AUTOLOAD_BACKEND ramps.
  const [isBackendAutoloadEnabled] = useFeatureFlagEnabled(FF.WORKLIST_AUTOLOAD_BACKEND);

  const [worklistAutoLoadRecoilState, setWorklistAutoLoadRecoilState] =
    useRecoilState(worklistAutoLoadState);
  const [activeCustomWorklistIDs] = useRecoilState(activeCustomWorklistIDsState);
  const currentCaseId = useCurrentCaseId();
  const { startRead } = useClaimPatient({ context });
  const { data } = useCurrentUser({ context });
  const location = useLocation();
  const isWorklist = useMemo(
    () => getPageType(location.pathname) === PageTypes.WORKLIST,
    [location.pathname]
  );
  const isReporter = useMemo(
    () => getPageType(location.pathname) === PageTypes.REPORTER,
    [location.pathname]
  );

  const userId = data?.me.id;

  const worklistSettings = data?.me.worklistSettings;

  const {
    surface,
    debouncedSearchForSurface: debouncedSearchV1,
    filters: filtersV1,
    availableFilters: availableFiltersV1,
    filtersBySurface,
    debouncedSearches,
    surfacesToDefaultFilters,
  } = useWorklistFiltersContext();

  const { filters: filtersV2 } = useWorklistFiltersContextV2();
  const { debouncedSearchForActiveWorklist: debouncedSearchV2 } = useWorklistSearchContext();
  const { availableFilters: availableFiltersV2 } = useGenerateFilterConfigV2({
    context,
    skipQueries: !isWorklist,
  });

  const { debouncedSearchForWorklist, filters, availableFilters } = useMemo(() => {
    if (isRefactorEnabled && surface === WorklistSurface.CUSTOM_WORKLIST) {
      return {
        debouncedSearchForWorklist: debouncedSearchV2,
        filters: filtersV2,
        availableFilters: availableFiltersV2,
      };
    }
    return {
      debouncedSearchForWorklist: debouncedSearchV1,
      filters: filtersV1,
      availableFilters: availableFiltersV1,
    };
  }, [
    isRefactorEnabled,
    surface,
    debouncedSearchV2,
    filtersV2,
    availableFiltersV2,
    debouncedSearchV1,
    filtersV1,
    availableFiltersV1,
  ]);

  const processedFilters = useMemo(
    () => (filters ? processFiltersForQuery(filters, availableFilters ?? {}) : {}),
    [filters, availableFilters]
  );

  // Computed each render from the worklist filter context. On the worklist page this
  // mirrors the table's query variables; the useEffect below captures it into recoil so
  // the autoload mutation can replay the same filters from the reporter page.
  const computedVariables = useWorklistVariables({
    userId,
    search: debouncedSearchForWorklist,
    surface,
    processedFilters,
    skipCustomWorklistsQuery: !isWorklist,
    context,
  });

  const { savedSortingPreferences } = useSavedSortingPreferences({ context });

  const [callAutoloadNextCase] = useMutation<
    AutoloadNextCaseMutation,
    AutoloadNextCaseMutationVariables
  >(AUTOLOAD_NEXT_CASE, { context });

  // TODO: WF-1969 - Remove once FF.WORKLIST_AUTOLOAD_BACKEND reaches 100% (legacy FE autoload path only)
  const [fetchNextAutoloadCase] = useLazyQuery(GET_WORKLIST, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (currentCaseId == null) {
      return;
    }

    // If the user manually re-opened a previously discarded case, allow it to be autoloadable again.
    removeDiscardedSmidInSessionStorage(currentCaseId);
  }, [currentCaseId]);

  const isWorklistAutoLoadToggleDisplayable = useMemo(() => {
    return isWorklistAutoLoadFeatureEnabled && displayableSurfaces.includes(surface);
  }, [isWorklistAutoLoadFeatureEnabled, surface]);

  const worklistAutoLoadOptions = useMemo(() => {
    return worklistSettings?.worklistAutoLoad ?? { surface: null };
  }, [worklistSettings]);
  const [updateWorklistSettings] = useMutation(UPDATE_WORKLIST_SETTINGS, {
    context,
  });

  /**
   * checks if the worklist auto load feature is enabled and
   * the current surface is the same as the one saved in the atom
   */
  const isWorklistAutoLoadEnabled = useMemo(() => {
    return isWorklistAutoLoadFeatureEnabled && surface === worklistAutoLoadRecoilState.surface;
  }, [isWorklistAutoLoadFeatureEnabled, surface, worklistAutoLoadRecoilState.surface]);

  /**
   * updates the worklist auto load atom with the current surface and search params
   */
  useEffect(() => {
    if (!isWorklist) {
      return;
    }

    if (worklistAutoLoadOptions?.surface === surface) {
      const sortingKey =
        surface === WorklistSurface.CUSTOM_WORKLIST && activeCustomWorklistIDs.length > 0
          ? `${surface}_${getWorklistKey(activeCustomWorklistIDs)}`
          : surface;

      setWorklistAutoLoadRecoilState({
        surface: worklistAutoLoadOptions?.surface as Nullable<WorklistSurfaceType>,
        searchParamsString: savedSortingPreferences?.[sortingKey] ?? null,
        variables: computedVariables,
      });
    }

    if (worklistAutoLoadOptions?.surface == null) {
      setWorklistAutoLoadRecoilState(worklistAutoLoadStateDefault);
    }
  }, [
    activeCustomWorklistIDs,
    computedVariables,
    isReporter,
    isWorklist,
    savedSortingPreferences,
    setWorklistAutoLoadRecoilState,
    surface,
    worklistAutoLoadOptions.surface,
    worklistAutoLoadRecoilState?.surface,
  ]);

  const toggleWorklistAutoLoad = useCallback(
    async (prevSurface?: WorklistSurfaceType) => {
      const updatedWorklistAutoLoad = {
        surface: isWorklistAutoLoadEnabled ? null : (prevSurface ?? surface),
      } as const;
      const updatedResponse = {
        ...worklistSettings,
        worklistAutoLoad: updatedWorklistAutoLoad,
      } as const;

      logger.info(
        `[useWorklistAutoLoad] - Attempting to toggle ${isWorklistAutoLoadEnabled ? 'OFF' : 'ON'} Worklist AutoLoad for ${surface} tab`,
        { surface, isWorklistAutoLoadEnabled }
      );

      await updateWorklistSettings({
        variables: { worklistAutoLoad: updatedWorklistAutoLoad },
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorklistSettings: {
            __typename: 'WorklistSettings',
            ...updatedResponse,
          },
        },
        update: (proxy) => {
          const { me } = proxy.readQuery<GetMeQuery, GetMeQueryVariables>({ query: GET_ME }) ?? {};
          proxy.writeQuery({
            query: GET_ME,
            data: {
              me: {
                ...me,
                worklistSettings: {
                  ...updatedResponse,
                },
              },
            },
          });
        },
        onError: (error): void => {
          logger.error(
            `[useWorklistAutoLoad] - Failed to toggle Worklist AutoLoad for ${surface} tab`,
            { error }
          );
        },
        onCompleted: async (): Promise<void> => {
          logger.info(
            `[useWorklistAutoLoad] - Successfully toggled Worklist AutoLoad for ${surface} tab`
          );

          const updatedSurface = updatedWorklistAutoLoad.surface as WorklistSurfaceType;
          const updatedSortingKey =
            updatedSurface === WorklistSurface.CUSTOM_WORKLIST && activeCustomWorklistIDs.length > 0
              ? `${updatedSurface}_${getWorklistKey(activeCustomWorklistIDs)}`
              : updatedSurface;

          // On toggle, capture the current worklist's computed variables. The next
          // sync useEffect tick will re-confirm/refresh this snapshot.
          setWorklistAutoLoadRecoilState({
            surface: updatedWorklistAutoLoad.surface,
            searchParamsString: savedSortingPreferences?.[updatedSortingKey] ?? null,
            variables: computedVariables,
          });
        },
      });
    },
    [
      activeCustomWorklistIDs,
      computedVariables,
      isWorklistAutoLoadEnabled,
      savedSortingPreferences,
      setWorklistAutoLoadRecoilState,
      surface,
      updateWorklistSettings,
      worklistSettings,
    ]
  );

  // Legacy: client-side eligibility filter against the FE-fetched worklist page.
  // The mutation path (FF on) gets this for free server-side.
  // TODO: WF-1969 - Remove once FF.WORKLIST_AUTOLOAD_BACKEND reaches 100%
  const getNextCase = useCallback(async () => {
    if (worklistAutoLoadRecoilState?.surface == null) return null;
    if (worklistAutoLoadRecoilState.variables == null) return null;

    const { data } = await fetchNextAutoloadCase({
      variables: worklistAutoLoadRecoilState.variables,
      context: {
        ...context,
        telemetry: {
          ...context.telemetry,
          fetchPolicy: 'network-only',
        },
      },
    });
    const items = (data?.worklistItems.items ?? []) as GetWorklistQuery['worklistItems']['items'];
    if (items.length === 0) return null;

    return items.find((item) => {
      // Skip the current case.
      if (currentCaseId === item.smid) return false;

      if (!AUTO_LOADABLE_STATUSES.includes(item.status)) {
        return false;
      }

      // Skip items claimed by another reader.
      if (item.claimedBy != null && item.claimedBy.id !== userId) return false;

      // Skip items mid-cancel (submitted but not yet sent).
      if (item.report?.submittedAt != null && item.report?.sentAt == null) return false;

      // In progress with a report already sent is a contradiction: the list is still showing
      // the pre-send status shortly after the countdown completed. The exam is really finalized,
      // so claiming it would silently start an addendum.
      if (item.status === WorklistItemStatus.InProgress && item.report?.sentAt != null) {
        return false;
      }

      // Skip items the user already discarded this session.
      if (loadDiscardedSmidsFromSessionStorage().has(item.smid)) return false;

      return true;
    });
  }, [
    worklistAutoLoadRecoilState?.surface,
    worklistAutoLoadRecoilState?.variables,
    fetchNextAutoloadCase,
    context,
    currentCaseId,
    userId,
  ]);

  const readNextCase = useCallback(
    async ({ reportExitAction }: { reportExitAction: ReportExitAction }) => {
      measureAutoloadNextCaseStart();

      if (worklistAutoLoadRecoilState?.surface == null) {
        return null;
      }

      // TODO: WF-1969 - Remove once FF.WORKLIST_AUTOLOAD_BACKEND reaches 100%
      if (!isBackendAutoloadEnabled) {
        // Legacy FE-side path: fetch a page, filter client-side, retry on claim conflicts.
        const MAX_RETRIES = 3;
        const attemptReadCase = async (
          retriesLeft: number = MAX_RETRIES
        ): Promise<string | null> => {
          try {
            const nextCase = await getNextCase();
            if (nextCase == null) {
              logger.info(`[useWorklistAutoLoad] - No next case found`);
              return null;
            }

            logger.info(
              `[useWorklistAutoLoad] - Autoloading WorklistItem ${nextCase.smid} after report exit action: ${reportExitAction}`
            );

            await startRead({
              overrideCaseId: nextCase.smid,
              overrideCaseGroupId: nextCase.groupId,
              overrideStudyIds: [], // prior_studies should be reset
            });

            measureAutoloadNextCaseEnd({
              exitAction: reportExitAction,
              preClaimed: nextCase.claimedBy != null,
            });

            return nextCase.smid;
          } catch (error: any) {
            logger.error(`[useWorklistAutoLoad] - Error fetching next case (legacy)`, { error });
            if (retriesLeft > 0 && isRetryableAutoloadError(error)) {
              logger.info(
                `[useWorklistAutoLoad] - Retrying... (${MAX_RETRIES - retriesLeft + 1}/${MAX_RETRIES})`
              );
              return attemptReadCase(retriesLeft - 1);
            }
            performance.clearMarksByName([marks.sirona.AutoloadNextCase.Start]);
            throw new Error(AUTOLOAD_GENERIC_ERROR);
          }
        };
        return attemptReadCase();
      }

      try {
        // The backend's autoload endpoint atomically picks and claims the next
        // eligible case using the stored worklist variables (filters, sort, search)
        // plus runtime exclusions (current case + session-discarded smids). Iteration
        // over concurrent claim conflicts happens server-side.
        const autoloadInput = buildAutoloadInput({
          variables: worklistAutoLoadRecoilState.variables,
          currentCaseSmid: currentCaseId ?? null,
          skippedSmids: Array.from(loadDiscardedSmidsFromSessionStorage()),
        });

        const result = await callAutoloadNextCase({
          variables: { input: autoloadInput },
        });

        const nextSmid = result?.data?.autoloadNextCase?.nextSmid ?? null;
        if (nextSmid == null) {
          logger.info(`[useWorklistAutoLoad] - No next case found`);
          return null;
        }

        logger.info(
          `[useWorklistAutoLoad] - Autoloading WorklistItem ${nextSmid} after report exit action: ${reportExitAction}`
        );

        await startRead({
          overrideCaseId: nextSmid,
          overrideCaseGroupId: undefined,
          overrideStudyIds: [], // prior_studies should be reset
        });

        measureAutoloadNextCaseEnd({
          exitAction: reportExitAction,
          preClaimed: false,
        });

        return nextSmid;
      } catch (error: any) {
        logger.error(`[useWorklistAutoLoad] - Error fetching next case`, { error });
        performance.clearMarksByName([marks.sirona.AutoloadNextCase.Start]);
        throw new Error(AUTOLOAD_GENERIC_ERROR);
      }
    },
    [
      callAutoloadNextCase,
      currentCaseId,
      getNextCase,
      isBackendAutoloadEnabled,
      startRead,
      worklistAutoLoadRecoilState?.surface,
      worklistAutoLoadRecoilState?.variables,
    ]
  );

  const handleAutoloadNextCase = useCallback(
    async ({
      reportExitAction,
      defaultToastMsg,
      onAutoloadComplete,
      isAddendum,
    }: HandleAutoloadNextCaseParams): Promise<HandleAutoloadNextCaseResult> => {
      const baseToastOptions = {
        yOffset: 45,
        icon: 'greenCheck',
        autoHideDuration: 8000,
      } as const;

      let toastMsg = defaultToastMsg;
      let toastOptions: Partial<ToastProps> = baseToastOptions;
      let nextCaseSmid: string | null = null;

      if (isWorklistAutoLoadEnabled) {
        if (currentCaseId != null) {
          logger.info(
            `[useWorklistAutoLoad] Attempting to autoLoad next case after ${reportExitAction} for worklistItem ${currentCaseId}`,
            {
              exitAction: reportExitAction,
              worklistItemSmid: currentCaseId,
              type: isAddendum ? 'addendum' : 'report',
            }
          );
        }

        try {
          nextCaseSmid = await readNextCase({ reportExitAction });

          if (nextCaseSmid == null) {
            toastMsg = `${defaultToastMsg} No more exams available to autoload.`;
          }
        } catch (error: any) {
          toastMsg = `${defaultToastMsg} ${error.message}`;
          toastOptions = {
            ...baseToastOptions,
            icon: 'redWarning',
          };
        }
      }

      onAutoloadComplete?.(nextCaseSmid);

      return {
        nextCaseSmid,
        toastMsg,
        toastOptions,
      };
    },
    [isWorklistAutoLoadEnabled, currentCaseId, readNextCase]
  );

  return {
    isWorklistAutoLoadEnabled,
    isWorklistAutoLoadToggleDisplayable,
    toggleWorklistAutoLoad,
    readNextCase,
    handleAutoloadNextCase,
    loadDiscardedSmidsFromSessionStorage,
    saveDiscardedSmidsInSessionStorage,
    removeDiscardedSmidInSessionStorage,
  };
};

export type UseMaybeAutoloadNextCaseParams = {
  onToastDisplayed?: (toastKey?: Nullable<ToastKey>) => void;
  previousToastKey?: Nullable<ToastKey>;
};

export type MaybeAutoloadNextCaseResult = {
  toastKey: Nullable<ToastKey>;
  nextCaseSmid: Nullable<string>;
  toastMsg: string;
  toastOptions: Partial<ToastProps>;
};

/**
 * Hook that wraps handleAutoloadNextCase with side effects:
 * - Shows toast notifications
 * - Clears patient context if feature enabled and extension available
 * - Manages patient jacket state
 * - Returns toast and next case info for caller to use if needed
 */
export const useMaybeAutoloadNextCase = ({
  onToastDisplayed,
  previousToastKey,
}: UseMaybeAutoloadNextCaseParams) => {
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);
  const isExtensionAvailable = isWorkspaceManagerAvailable();
  const { managePatientJacket } = useManagePatientJacket({ context: APOLLO_QUERY_CONTEXT });
  const { isWorklistAutoLoadEnabled, handleAutoloadNextCase } = useWorklistAutoLoad({
    context: APOLLO_QUERY_CONTEXT,
  });
  const currentCaseId = useCurrentCaseId();
  const { enqueueOrUpdateToast, closeToast } = useToasterDispatch();

  return useCallback(
    async (
      isEnabled: boolean,
      defaultToastMsg: string,
      autoloadParams: HandleAutoloadNextCaseParams
    ): Promise<MaybeAutoloadNextCaseResult> => {
      let toastMsg = defaultToastMsg;
      const baseToastOptions: Partial<ToastProps> = {
        icon: 'greenCheck',
        severity: 'default',
        actionColor: 'info',
        position: 'top-right',
        yOffset: 45,
        autoHideDuration: 8000,
      };

      let nextCaseSmid: Nullable<string> = null;
      let toastOptions: Partial<ToastProps> = baseToastOptions;

      if (isEnabled) {
        logger.info(
          `[useMaybeAutoloadNextCase] Attempting to autoLoad next case after ${autoloadParams.reportExitAction} for worklistItem ${currentCaseId}`
        );

        const result = await handleAutoloadNextCase(autoloadParams);
        toastMsg = result?.toastMsg;
        toastOptions = { ...baseToastOptions, ...result?.toastOptions };
        nextCaseSmid = result?.nextCaseSmid;
      }

      if (
        (!nextCaseSmid || !isWorklistAutoLoadEnabled) &&
        isClearPatientContextEnabled &&
        isExtensionAvailable
      ) {
        dispatchExtensionEvent(clearCaseEvent());
      }

      const toastKey = enqueueOrUpdateToast(
        toastMsg,
        previousToastKey ?? null,
        toastOptions as ToastProps
      );
      onToastDisplayed?.(toastKey);

      managePatientJacket({ nextCaseSmid });

      return { toastKey, nextCaseSmid, toastMsg, toastOptions };
    },
    [
      isWorklistAutoLoadEnabled,
      isClearPatientContextEnabled,
      isExtensionAvailable,
      enqueueOrUpdateToast,
      managePatientJacket,
      currentCaseId,
      handleAutoloadNextCase,
      onToastDisplayed,
      previousToastKey,
    ]
  );
};

const APOLLO_QUERY_CONTEXT = { telemetry: { component: 'useMaybeAutoloadNextCase' } };
